import {ComponentPropsWithoutRef, FC, forwardRef, ReactNode, useMemo, useState} from 'react'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {TKey, useTranslation} from '@/translations/i18n.tsx'
import {ChevronDownIcon} from '@components/ui/icon'
import {cn} from '@utilities/helpers'
import {InputText} from '@components/commons/input-text.tsx'
import {Radio, RadioGroup} from '@components/commons/radio.tsx'
import {Checkbox} from '@components/commons/checkbox.tsx'
import {Button} from '@components/ui/button.tsx'
import {Spinner} from '@components/ui/spinner'
import {Label} from '@components/ui/label.tsx'
import {InputHelpText} from '@components/ui/input-help-text.tsx'
import {Select, SelectContent, SelectItem, SelectTrigger} from '@components/ui/select.tsx'
import {
    Combobox,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger
} from '@components/ui/combobox-atoms.tsx'
import {toast} from '@utilities/toast'
import {COUNTRY_NAMES_BY_CODE} from '@features/floor-plan/country-names.ts'
import {useFloorPlanDevices} from '@features/floor-plan/queries/useFloorPlanDevices.ts'
import {useFloorPlanOptInSubmit} from '@features/floor-plan/queries/useFloorPlanOptInSubmit.ts'
import {FloorPlanOptInDefaults} from '@features/floor-plan/queries/useFloorPlanOptInDefaults.ts'
import {DEVICE_NOT_LISTED_VALUE} from '@features/floor-plan/constants.ts'

const ValidationSchema = z.object({
    // The Airbnb User ID may be submitted blank (see field help text).
    airbnb_user_id: z.string(),
    email: z.string().min(1, {message: 'errors:required'}).email({message: 'errors:email_invalid'}),
    first_name: z.string().min(1, {message: 'errors:required'}),
    last_name: z.string().min(1, {message: 'errors:required'}),
    city: z.string().min(1, {message: 'errors:required'}),
    country: z.string().min(1, {message: 'errors:required'}),
    contact_preference: z.boolean({required_error: 'errors:required', invalid_type_error: 'errors:required'}),
    device: z.string().min(1, {message: 'errors:required'}),
    terms_accepted: z.boolean().refine(value => value, {message: 'errors:required'})
})

type ValidationSchema = z.infer<typeof ValidationSchema>

interface SelectFieldProps {
    label: ReactNode
    helpText?: string
    placeholder: ReactNode
    error?: ReactNode
    value: string
    onChange: (value: string) => void
    options: {value: string; label: string}[]
    disabled?: boolean
}

/** Inline error + retry shown under a select when its lookup query fails. */
const OptionsLoadError: FC<{onRetry: () => void}> = ({onRetry}) => {
    const {t} = useTranslation()
    return (
        <>
            {t('floor_plan:opt_in:options_error')}{' '}
            <button
                type="button"
                onClick={onRetry}
                className="cursor-pointer border-none bg-none p-0 font-medium text-purple-700 text-sm underline"
            >
                {t('floor_plan:opt_in:options_retry')}
            </button>
        </>
    )
}

const SelectField: FC<SelectFieldProps> = ({
    label,
    helpText,
    placeholder,
    error,
    value,
    onChange,
    options,
    disabled
}) => (
    <div className="flex flex-col gap-1.5">
        <Label>{label}</Label>
        <Select value={value} onValueChange={next => next && onChange(next)}>
            <SelectTrigger className="w-full" disabled={disabled} placeholder={placeholder}>
                {(selected: string) => options.find(option => option.value === selected)?.label}
            </SelectTrigger>
            <SelectContent>
                {options.map(option => (
                    <SelectItem
                        className="block wrap-anywhere whitespace-normal"
                        key={option.value}
                        value={option.value}
                    >
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
        <InputHelpText error={error} helpText={helpText} />
    </div>
)

interface SearchableSelectFieldProps {
    label: ReactNode
    placeholder: ReactNode
    searchPlaceholder: string
    error?: ReactNode
    value: string
    onChange: (value: string) => void
    options: {value: string; label: string}[]
}

/**
 * Trigger button for the searchable select: mirrors the styled Select trigger.
 * Forwards ref and the props Base UI injects (data-popup-open, onClick, …) so
 * the combobox opens correctly when used as `ComboboxTrigger`'s `render`.
 */
const SearchableSelectTrigger = forwardRef<
    HTMLButtonElement,
    ComponentPropsWithoutRef<'button'> & {placeholder: ReactNode; label?: string}
>(({placeholder, label, className, ...rest}, ref) => (
    <button
        ref={ref}
        type="button"
        className={cn(
            'group inline-flex h-10 w-full cursor-pointer select-none items-center justify-between gap-1 rounded-lg bg-white px-5 font-normal text-neutral-900 text-sm shadow-xs outline-1 -outline-offset-1 outline-neutral-300 transition-all',
            'hover:bg-neutral-50 focus-visible:shadow-[0_0_0_4px] focus-visible:shadow-primary-100 focus-visible:outline-primary-300 data-popup-open:shadow-[0_0_0_4px] data-popup-open:shadow-primary-100 data-popup-open:outline-primary-300',
            className
        )}
        {...rest}
    >
        <span className={cn('flex flex-1 truncate text-left font-semibold', !label && 'font-normal text-neutral-400')}>
            {label ?? placeholder}
        </span>
        <ChevronDownIcon
            className="ml-auto fill-neutral-500 transition-transform duration-200 ease-in-out in-data-popup-open:rotate-180"
            size={16}
        />
    </button>
))
SearchableSelectTrigger.displayName = 'SearchableSelectTrigger'

/** Select with a search input in the popover (for long lists like countries). */
const SearchableSelectField: FC<SearchableSelectFieldProps> = ({
    label,
    placeholder,
    searchPlaceholder,
    error,
    value,
    onChange,
    options
}) => {
    const [search, setSearch] = useState('')

    const selectedLabel = useMemo(() => options.find(option => option.value === value)?.label, [options, value])
    const matches = useMemo(() => {
        const query = search.trim().toLowerCase()
        return query ? options.filter(option => option.label.toLowerCase().includes(query)) : options
    }, [options, search])

    return (
        <div className="flex flex-col gap-1.5">
            <Label>{label}</Label>
            <Combobox
                items={matches.map(option => option.value)}
                value={value}
                filter={null}
                onInputValueChange={setSearch}
                onValueChange={next => next && onChange(next)}
            >
                <ComboboxTrigger
                    nativeButton={false}
                    render={<SearchableSelectTrigger placeholder={placeholder} label={selectedLabel} />}
                />

                <ComboboxContent className="grid max-h-100 grid-rows-[auto_minmax(0,1fr)] gap-1 overflow-y-hidden rounded-lg border-neutral-200 px-1.5 py-1 shadow-lg">
                    <ComboboxInput inputSize="sm" placeholder={searchPlaceholder} />
                    <ComboboxList className="overflow-y-auto scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        {matches.map(option => (
                            <ComboboxItem
                                className="my-px block gap-0 rounded-md p-0 px-2 py-2.5 text-neutral-900 text-md whitespace-normal transition-all wrap-anywhere hover:bg-transparent data-disabled:text-neutral-400 data-highlighted:bg-transparent data-highlighted:not-data-selected:bg-neutral-50 data-selected:bg-primary-100"
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </ComboboxItem>
                        ))}
                    </ComboboxList>
                </ComboboxContent>
            </Combobox>
            <InputHelpText error={error} />
        </div>
    )
}

export const FloorPlanOptInForm: FC<{defaults: FloorPlanOptInDefaults; onSuccess: () => void}> = ({
    defaults,
    onSuccess
}) => {
    const {t} = useTranslation()
    const devicesQuery = useFloorPlanDevices()
    const submitMutation = useFloorPlanOptInSubmit()

    const countryOptions = useMemo(
        () =>
            Object.values(COUNTRY_NAMES_BY_CODE)
                .sort((a, b) => a.localeCompare(b))
                .map(name => ({value: name, label: name})),
        []
    )
    const deviceOptions = useMemo(
        () => [
            ...(devicesQuery.data ?? []).map(device => ({value: device.id, label: device.label})),
            {value: DEVICE_NOT_LISTED_VALUE, label: t('floor_plan:opt_in:device_not_listed')}
        ],
        [devicesQuery.data, t]
    )

    const {
        control,
        register,
        handleSubmit,
        formState: {errors}
    } = useForm<ValidationSchema>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(ValidationSchema),
        defaultValues: {
            airbnb_user_id: defaults.airbnbUserId,
            email: defaults.email,
            first_name: defaults.firstName,
            last_name: defaults.lastName,
            city: defaults.city,
            country: defaults.country,
            device: '',
            terms_accepted: false
        }
    })

    const onSubmit = handleSubmit(async values => {
        try {
            await submitMutation.mutateAsync(values)
            onSuccess()
        } catch {
            toast.error(t('floor_plan:opt_in:error_toast'))
        }
    })

    const errorText = (key?: string) => t((key ?? '') as TKey)

    // "Other / not listed" device can't capture scans: replace the terms field
    // with a note and disable submit.
    const deviceIncompatible = useWatch({control, name: 'device'}) === DEVICE_NOT_LISTED_VALUE

    return (
        <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 max-md:grid-cols-1">
                <InputText
                    type="text"
                    label={t('floor_plan:opt_in:user_id_label')}
                    helpText={t('floor_plan:opt_in:user_id_help')}
                    errorMessage={errorText(errors.airbnb_user_id?.message)}
                    {...register('airbnb_user_id')}
                />
                <InputText
                    type="text"
                    label={t('floor_plan:opt_in:email_label')}
                    helpText={t('floor_plan:opt_in:email_help')}
                    errorMessage={errorText(errors.email?.message)}
                    readOnly
                    disabled
                    {...register('email')}
                />
                <InputText
                    type="text"
                    label={t('floor_plan:opt_in:first_name_label')}
                    errorMessage={errorText(errors.first_name?.message)}
                    {...register('first_name')}
                />
                <InputText
                    type="text"
                    label={t('floor_plan:opt_in:last_name_label')}
                    errorMessage={errorText(errors.last_name?.message)}
                    {...register('last_name')}
                />
                <InputText
                    type="text"
                    label={t('floor_plan:opt_in:city_label')}
                    errorMessage={errorText(errors.city?.message)}
                    {...register('city')}
                />
                <Controller
                    name="country"
                    control={control}
                    render={({field}) => (
                        <SearchableSelectField
                            label={t('floor_plan:opt_in:country_label')}
                            placeholder={t('floor_plan:opt_in:country_placeholder')}
                            searchPlaceholder={t('floor_plan:opt_in:country_search_placeholder')}
                            error={errorText(errors.country?.message)}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            options={countryOptions}
                        />
                    )}
                />

                <div className="flex flex-col gap-1.5">
                    <Label>{t('floor_plan:opt_in:contact_label')}</Label>
                    <InputHelpText helpText={t('floor_plan:opt_in:contact_help')} />
                    <Controller
                        name="contact_preference"
                        control={control}
                        render={({field}) => (
                            <RadioGroup
                                onValueChange={(value: string) => field.onChange(value === 'true' ? true : false)}
                                value={field.value !== undefined ? field.value.toString() : ''}
                                className="flex flex-col gap-1"
                                name="contact_preference"
                            >
                                <Radio id="contact_yes" label={t('floor_plan:opt_in:contact_yes')} value="true" />
                                <Radio id="contact_no" label={t('floor_plan:opt_in:contact_no')} value="false" />
                            </RadioGroup>
                        )}
                    />
                    <InputHelpText error={errorText(errors.contact_preference?.message)} />
                </div>

                <div className="col-span-full">
                    <h3 className="mt-2 font-semibold text-lg text-neutral-900">
                        {t('floor_plan:opt_in:equipment_agreement_title')}
                    </h3>
                </div>

                <div className="flex flex-col gap-4">
                    <Controller
                        name="device"
                        control={control}
                        render={({field}) => (
                            <SelectField
                                label={t('floor_plan:opt_in:required_equipment_label')}
                                helpText={t('floor_plan:opt_in:required_equipment_help')}
                                placeholder={t('floor_plan:opt_in:device_placeholder')}
                                error={
                                    devicesQuery.isError ? (
                                        <OptionsLoadError onRetry={() => void devicesQuery.refetch()} />
                                    ) : (
                                        errorText(errors.device?.message)
                                    )
                                }
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                options={deviceOptions}
                                disabled={devicesQuery.isLoading}
                            />
                        )}
                    />

                    {deviceIncompatible ? (
                        <p className="m-0 text-md text-neutral-900">{t('floor_plan:opt_in:device_incompatible')}</p>
                    ) : (
                        <div className="flex flex-col gap-1.5">
                            <Label>{t('floor_plan:opt_in:terms_label')}</Label>

                            <Controller
                                name="terms_accepted"
                                control={control}
                                render={({field}) => (
                                    <Checkbox
                                        id={field.name}
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        errorMessage={errorText(errors.terms_accepted?.message)}
                                        label={t('floor_plan:opt_in:terms_help')}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end [&>button]:w-full md:[&>button]:w-fit">
                <Button type="submit" size="lg" disabled={submitMutation.isPending || deviceIncompatible}>
                    {t('floor_plan:opt_in:submit')}
                    {submitMutation.isPending && <Spinner size={20} />}
                </Button>
            </div>
        </form>
    )
}
